/**
 * OrderShopService
 * Service for public shop order endpoints.
 * Route: api/shop
 * Controller: ShopController.cs (CCMS.Api.OrderManagement)
 */
import { BaseService } from '../base/BaseService';
import { validateOrderLineRequest } from '../../utils/validation';
/**
 * OrderShopService
 * Public shop endpoints for order tracking, placement, and status lookups.
 * Read endpoints are anonymous. Write endpoints require authentication.
 * All read data is served from Redis cache on the backend.
 */
export class OrderShopService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? OrderShopService.DEFAULT_SERVICE_PATH,
            basePath: OrderShopService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== ORDER TRACKING (Anonymous) ====================
    /**
     * Track an order by order number
     * GET /api/shop/orders/track/{orderNumber}
     */
    async trackOrder(orderNumber) {
        const response = await this.trackOrderRaw(orderNumber);
        return response.data ?? null;
    }
    /**
     * Track order by number (raw response)
     */
    trackOrderRaw(orderNumber) {
        const endpoint = this.getEndpoint('track-order', `orders/track/${encodeURIComponent(orderNumber)}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Track an order by reference number
     * GET /api/shop/orders/track-ref/{referenceNo}
     */
    async trackOrderByRef(referenceNo) {
        const response = await this.trackOrderByRefRaw(referenceNo);
        return response.data ?? null;
    }
    /**
     * Track order by reference (raw response)
     */
    trackOrderByRefRaw(referenceNo) {
        const endpoint = this.getEndpoint('track-order-ref', `orders/track-ref/${encodeURIComponent(referenceNo)}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== STATUS LOOKUPS (Anonymous) ====================
    /**
     * Get all order statuses
     * GET /api/shop/order-statuses
     */
    async getOrderStatuses() {
        const response = await this.getOrderStatusesRaw();
        return response.data ?? [];
    }
    /**
     * Get order statuses (raw response)
     */
    getOrderStatusesRaw() {
        const endpoint = this.getEndpoint('order-statuses', 'order-statuses');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get all payment statuses
     * GET /api/shop/payment-statuses
     */
    async getPaymentStatuses() {
        const response = await this.getPaymentStatusesRaw();
        return response.data ?? [];
    }
    /**
     * Get payment statuses (raw response)
     */
    getPaymentStatusesRaw() {
        const endpoint = this.getEndpoint('payment-statuses', 'payment-statuses');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get all order line statuses
     * GET /api/shop/order-line-statuses
     */
    async getOrderLineStatuses() {
        const response = await this.getOrderLineStatusesRaw();
        return response.data ?? [];
    }
    /**
     * Get order line statuses (raw response)
     */
    getOrderLineStatusesRaw() {
        const endpoint = this.getEndpoint('order-line-statuses', 'order-line-statuses');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== ORDER CREATION (Anonymous) ====================
    /**
     * Create a new order with lines, add-ons, and variants (no payments).
     * POST /api/shop/orders/create
     * Publishes OrderPlacedEvent and per-vendor VendorOrderPlacedEvent on the backend.
     */
    async createOrder(request) {
        // Validate line amounts before sending to API
        if (request.lines) {
            for (const line of request.lines) {
                validateOrderLineRequest(line);
            }
        }
        const response = await this.createOrderRaw(request);
        if (!response.data) {
            throw new Error('Failed to create order');
        }
        return response.data;
    }
    /**
     * Create order (raw response)
     */
    createOrderRaw(request) {
        // Validate line amounts before sending to API
        if (request.lines) {
            for (const line of request.lines) {
                validateOrderLineRequest(line);
            }
        }
        const endpoint = this.getEndpoint('create-order', 'orders/create');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    // ==================== ORDER EDITING (Anonymous) ====================
    /**
     * Edit an existing order (only when StatusID == 0 / Pending).
     * PUT /api/shop/orders/{orderId}/edit
     * Uses diff-based reconciliation:
     * - Lines with lineID > 0 are updated; without lineID are created; missing lines are deleted.
     * - Same pattern for add-ons (lineAddOnID) and variants (lineVariantID).
     * Updates Redis cache on the backend.
     */
    async editOrder(orderId, request) {
        const response = await this.editOrderRaw(orderId, request);
        if (!response.data) {
            throw new Error('Failed to edit order');
        }
        return response.data;
    }
    /**
     * Edit order (raw response)
     */
    editOrderRaw(orderId, request) {
        const endpoint = this.getEndpoint('edit-order', `orders/${orderId}/edit`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, request);
    }
    // ==================== ORDER PLACEMENT (Authenticated) ====================
    /**
     * Place a new order
     * POST /api/shop/orders
     * Requires Customer, Staff, or Volunteer role
     * @deprecated Use createOrder instead
     */
    async placeOrder(request) {
        // Validate line amounts before sending to API
        if (request.lines) {
            for (const line of request.lines) {
                validateOrderLineRequest(line);
            }
        }
        const response = await this.placeOrderRaw(request);
        if (!response.data) {
            throw new Error('Failed to place order');
        }
        return response.data;
    }
    /**
     * Place order (raw response)
     * @deprecated Use createOrderRaw instead
     */
    placeOrderRaw(request) {
        // Validate line amounts before sending to API
        if (request.lines) {
            for (const line of request.lines) {
                validateOrderLineRequest(line);
            }
        }
        const endpoint = this.getEndpoint('place-order', 'orders');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, request);
    }
    /**
     * Get the authenticated user's orders
     * GET /api/shop/orders/mine
     * Requires Customer, Staff, or Volunteer role
     */
    async getMyOrders() {
        const response = await this.getMyOrdersRaw();
        return response.data ?? [];
    }
    /**
     * Get my orders (raw response)
     */
    getMyOrdersRaw() {
        const endpoint = this.getEndpoint('my-orders', 'orders/mine');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
}
OrderShopService.DEFAULT_SERVICE_PATH = 'orders';
OrderShopService.BASE_PATH = 'api/shop';
//# sourceMappingURL=OrderShopService.js.map